import { useCallback, useEffect, useMemo, useState } from "react";
import { ADMIN_MONITORING_API_BASE_URL, buildAdminMonitoringUrl } from "../services/adminMonitoringApi";

const ADMIN_KEY_STORAGE = "pennypantry_admin_api_key";

function AdminCard({ title, children }) {
  return (
    <section className="admin-monitor-card">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function JsonBlock({ data }) {
  return <pre className="admin-monitor-json">{JSON.stringify(data, null, 2)}</pre>;
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return String(value);
  }
}

function todayFileDate() {
  return new Date().toISOString().slice(0, 10);
}

function downloadBlob(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function buildAdminFetchError(path, response) {
  let details = "";
  try {
    details = await response.text();
  } catch (error) {
    details = "";
  }

  if (response.status === 401 || response.status === 403) {
    return new Error(`Admin key rejected (${response.status}) for ${path}. Check X-Admin-Key.`);
  }
  if (response.status >= 500) {
    return new Error(`Backend error (${response.status}) for ${path}. ${details.slice(0, 240)}`);
  }
  return new Error(`Request failed (${response.status}) for ${path}. ${details.slice(0, 240)}`);
}

function buildNetworkErrorMessage(path, error) {
  const message = error?.message || "";
  if (/failed to fetch|networkerror|load failed/i.test(message)) {
    return (
      `Network/CORS failure while calling ${buildAdminMonitoringUrl(path)}. ` +
      "Confirm the API allows origin https://admin.pennypantryapp.com and header X-Admin-Key."
    );
  }
  return message || "Could not load monitoring.";
}

export default function AdminMonitorPage() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [draftKey, setDraftKey] = useState(adminKey);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [errorDetails, setErrorDetails] = useState("");
  const [payloads, setPayloads] = useState({});

  const headers = useMemo(() => ({ "X-Admin-Key": adminKey }), [adminKey]);

  const fetchAdmin = useCallback(
    async (path) => {
      const response = await fetch(buildAdminMonitoringUrl(path), { headers });
      if (!response.ok) {
        throw await buildAdminFetchError(path, response);
      }
      return response.json();
    },
    [headers]
  );

  const downloadErrors = useCallback(
    async (format) => {
      if (!adminKey) {
        setStatus("Enter your admin key before exporting errors.");
        return;
      }
      setStatus(`Preparing ${format.toUpperCase()} export...`);
      setErrorDetails("");
      try {
        const path = `/admin/monitoring/errors/export?format=${format}&limit=500&since_hours=168&include_requests=true`;
        const response = await fetch(buildAdminMonitoringUrl(path), { headers });
        if (!response.ok) {
          throw await buildAdminFetchError(path, response);
        }
        const content = await response.text();
        downloadBlob(
          content,
          `pennypantry-errors-${todayFileDate()}.${format}`,
          format === "csv" ? "text/csv" : "application/json"
        );
        setStatus(`Downloaded ${format.toUpperCase()} error export.`);
      } catch (error) {
        setStatus("Could not export errors.");
        setErrorDetails(buildNetworkErrorMessage("/admin/monitoring/errors/export", error));
      }
    },
    [adminKey, headers]
  );

  const copySummaryForChatGPT = useCallback(async () => {
    const overviewData = payloads.overview || {};
    const healthData = payloads.health || {};
    const errorRows = payloads.errors?.errors || [];
    const topEndpoints = overviewData.error_endpoints || [];
    const slowEndpoints = overviewData.slow_endpoints || payloads.requests?.slow_endpoints || [];
    const summary = [
      "PennyPantry admin monitoring summary",
      `Generated at: ${new Date().toISOString()}`,
      `Status: ${overviewData.status || "-"}`,
      `Errors 24h: ${overviewData.errors_24h ?? "-"}`,
      `5xx 24h: ${overviewData.five_xx_24h ?? "-"}`,
      `Markets: ${overviewData.markets_total ?? "-"}`,
      `Products: ${overviewData.products_total ?? "-"}`,
      `Prices: ${overviewData.prices_total ?? "-"}`,
      `Fresh today: ${healthData.prices_updated_today ?? overviewData.prices_updated_today ?? "-"}`,
      `Fresh 7d: ${healthData.prices_updated_7d ?? overviewData.prices_updated_7d ?? "-"}`,
      `Stale prices: ${healthData.stale_prices_count ?? overviewData.stale_prices_count ?? "-"}`,
      "",
      "Feature flags:",
      JSON.stringify(overviewData.feature_flags || {}, null, 2),
      "",
      "Top error endpoints:",
      JSON.stringify(topEndpoints, null, 2),
      "",
      "Slow endpoints:",
      JSON.stringify(slowEndpoints, null, 2),
      "",
      "Latest 10 errors:",
      JSON.stringify(
        errorRows.slice(0, 10).map((item) => ({
          created_at: item.created_at,
          source: item.source,
          endpoint: item.endpoint,
          status_code: item.status_code,
          message: item.message,
        })),
        null,
        2
      ),
    ].join("\n");
    try {
      await navigator.clipboard.writeText(summary);
      setStatus("Copied safe ChatGPT summary.");
    } catch (error) {
      setStatus("Clipboard unavailable. Summary is shown in console.");
      console.info(summary);
    }
  }, [payloads]);

  const refresh = useCallback(async () => {
    if (!adminKey) {
      setStatus("Enter your admin key to load monitoring.");
      return;
    }
    setLoading(true);
    setStatus("Loading production monitoring...");
    setErrorDetails("");
    try {
      const [
        overview,
        errors,
        requests,
        health,
        featureFlags,
        providers,
        discovery,
        usersBasic,
      ] = await Promise.all([
        fetchAdmin("/admin/monitoring/overview"),
        fetchAdmin("/admin/monitoring/errors?limit=25"),
        fetchAdmin("/admin/monitoring/requests?limit=25"),
        fetchAdmin("/admin/monitoring/health"),
        fetchAdmin("/admin/monitoring/feature-flags"),
        fetchAdmin("/admin/monitoring/providers"),
        fetchAdmin("/admin/monitoring/discovery"),
        fetchAdmin("/admin/monitoring/users-basic"),
      ]);
      setPayloads({ overview, errors, requests, health, featureFlags, providers, discovery, usersBasic });
      setStatus(`Updated ${new Date().toLocaleTimeString()}`);
    } catch (error) {
      setStatus("Could not load production monitoring.");
      setErrorDetails(buildNetworkErrorMessage("/admin/monitoring/overview", error));
    } finally {
      setLoading(false);
    }
  }, [adminKey, fetchAdmin]);

  useEffect(() => {
    if (adminKey) {
      refresh();
    }
  }, [adminKey, refresh]);

  const overview = payloads.overview || {};
  const recentErrors = payloads.errors?.errors || [];
  const requests = payloads.requests || {};
  const flags = payloads.featureFlags?.feature_flags || overview.feature_flags || {};

  return (
    <main className="admin-monitor-shell">
      <header className="admin-monitor-hero">
        <div>
          <p className="eyebrow">PennyPantry Production</p>
          <h1>Admin Monitor</h1>
          <p>Errors, latency, data health, providers and feature flags in one protected screen.</p>
          <small>API: {ADMIN_MONITORING_API_BASE_URL}</small>
        </div>
        <div className="admin-monitor-keybox">
          <input
            type="password"
            value={draftKey}
            placeholder="X-Admin-Key"
            onChange={(event) => setDraftKey(event.target.value)}
          />
          <button
            onClick={() => {
              localStorage.setItem(ADMIN_KEY_STORAGE, draftKey);
              setAdminKey(draftKey);
            }}
          >
            Save key
          </button>
        <button onClick={refresh} disabled={loading || !adminKey}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button onClick={() => downloadErrors("json")} disabled={loading || !adminKey}>
            Download errors JSON
          </button>
          <button onClick={() => downloadErrors("csv")} disabled={loading || !adminKey}>
            Download errors CSV
          </button>
          <button onClick={copySummaryForChatGPT} disabled={loading || !adminKey}>
            Copy summary for ChatGPT
          </button>
        </div>
      </header>

      <p className="admin-monitor-status">{status}</p>
      {errorDetails ? <p className="admin-monitor-error">{errorDetails}</p> : null}

      <section className="admin-monitor-grid admin-monitor-kpis">
        <AdminCard title="Status">
          <strong>{overview.status || "-"}</strong>
          <span>DB latency: {overview.db_latency_ms ?? "-"} ms</span>
          <span>Revision: {overview.alembic_revision || "-"}</span>
        </AdminCard>
        <AdminCard title="Errors 24h">
          <strong>{overview.errors_24h ?? "-"}</strong>
          <span>5xx: {overview.five_xx_24h ?? "-"}</span>
        </AdminCard>
        <AdminCard title="Data">
          <strong>{overview.prices_total ?? "-"}</strong>
          <span>Markets: {overview.markets_total ?? "-"}</span>
          <span>Products: {overview.products_total ?? "-"}</span>
        </AdminCard>
        <AdminCard title="Users">
          <strong>{payloads.usersBasic?.users_total ?? "-"}</strong>
          <span>Tokens: {payloads.usersBasic?.device_tokens_total ?? "-"}</span>
          <span>Push enabled: {payloads.usersBasic?.push_enabled_tokens ?? "-"}</span>
        </AdminCard>
        <AdminCard title="Price Freshness">
          <strong>{payloads.health?.prices_updated_today ?? overview.prices_updated_today ?? "-"}</strong>
          <span>Last 7d: {payloads.health?.prices_updated_7d ?? overview.prices_updated_7d ?? "-"}</span>
          <span>Stale: {payloads.health?.stale_prices_count ?? overview.stale_prices_count ?? "-"}</span>
        </AdminCard>
      </section>

      <section className="admin-monitor-grid">
        <AdminCard title="Recent Errors">
          <div className="admin-monitor-list">
            {recentErrors.length ? recentErrors.map((item) => (
              <article key={item.id} className="admin-monitor-row">
                <strong>{item.level} {item.status_code || ""}</strong>
                <span>{item.method} {item.endpoint}</span>
                <small>{formatDate(item.created_at)}</small>
                <p>{item.message}</p>
              </article>
            )) : <p>No recent errors captured.</p>}
          </div>
        </AdminCard>

        <AdminCard title="Slow Endpoints">
          <div className="admin-monitor-list">
            {(requests.slow_endpoints || overview.slow_endpoints || []).map((item) => (
              <article key={`${item.method}-${item.endpoint}`} className="admin-monitor-row">
                <strong>{item.avg_duration_ms} ms</strong>
                <span>{item.method} {item.endpoint}</span>
                <small>{item.count} samples</small>
              </article>
            ))}
          </div>
        </AdminCard>

        <AdminCard title="Feature Flags">
          <JsonBlock data={flags} />
        </AdminCard>

        <AdminCard title="Provider Status">
          <JsonBlock data={payloads.providers?.providers || overview.providers || {}} />
        </AdminCard>

        <AdminCard title="Data Health">
          <JsonBlock data={payloads.health || {}} />
        </AdminCard>

        <AdminCard title="Source Freshness">
          <JsonBlock
            data={{
              source_type_freshness_breakdown:
                payloads.health?.source_type_freshness_breakdown ||
                overview.source_type_freshness_breakdown ||
                [],
              source_type_updated_7d_breakdown:
                payloads.health?.source_type_updated_7d_breakdown ||
                overview.source_type_updated_7d_breakdown ||
                [],
            }}
          />
        </AdminCard>

        <AdminCard title="Discovery Jobs">
          <JsonBlock data={payloads.discovery || {}} />
        </AdminCard>

        <AdminCard title="Users Basic">
          <JsonBlock data={payloads.usersBasic || {}} />
        </AdminCard>

        <AdminCard title="Recent Requests">
          <JsonBlock data={(requests.requests || []).slice(0, 10)} />
        </AdminCard>
      </section>
    </main>
  );
}
